/**
 * Interface Detail View page.
 * Visualizes a single interface with its parent switch and VLANs.
 * Implements FR-3.3.2: Interface Detail View
 */

import { FC, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TableSortLabel,
  Grid,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Router as RouterIcon,
} from '@mui/icons-material';
import { GraphVisualization, GraphControls } from '@/components/Graph';
import api from '@/services/api';
import type {
  InterfaceQueryResponse,
  InterfaceSummary,
  GraphData,
  GraphNode,
  GraphEdge,
  VLAN,
  InterfaceRelationshipInfo,
} from '@/types/entities';

type SortField = 'vlanId' | 'name' | 'status';
type SortOrder = 'asc' | 'desc';

export const InterfaceDetailView: FC = () => {
  const graphContainerRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedInterface, setSelectedInterface] = useState<InterfaceSummary | null>(null);
  const [interfaces, setInterfaces] = useState<InterfaceSummary[]>([]);
  const [interfacesLoading, setInterfacesLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [interfaceData, setInterfaceData] = useState<InterfaceQueryResponse | null>(null);
  const [sortField, setSortField] = useState<SortField>('vlanId');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const abortControllerRef = useRef<AbortController | null>(null);
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  const [autocompleteOpen, setAutocompleteOpen] = useState<boolean>(false);

  /**
   * Load all interfaces on component mount
   */
  useEffect(() => {
    const loadInterfaces = async () => {
      setInterfacesLoading(true);
      try {
        const response = await api.getInterfaces();
        setInterfaces(response.interfaces);
      } catch (err) {
        console.error('Failed to load interfaces:', err);
        // Don't show error for interface list loading - user can still type interface ID
      } finally {
        setInterfacesLoading(false);
      }
    };

    loadInterfaces();
  }, []);

  /**
   * Auto-focus and open autocomplete when navigating from dashboard
   */
  useEffect(() => {
    if (searchParams.get('focus') === 'true' && interfaces.length > 0) {
      // Use setTimeout to ensure component is fully mounted and DOM is ready
      setTimeout(() => {
        // Focus the input
        autocompleteInputRef.current?.focus();
        // Open the dropdown
        setAutocompleteOpen(true);
      }, 100);

      // Clean up URL parameter to prevent re-triggering on re-renders
      const params = new URLSearchParams(window.location.search);
      params.delete('focus');
      window.history.replaceState({}, '', `${window.location.pathname}`);
    }
  }, [searchParams, interfaces]);

  /**
   * Auto-load interface from URL query parameter
   */
  useEffect(() => {
    const interfaceIdParam = searchParams.get('id');
    if (interfaceIdParam && interfaces.length > 0 && !loading) {
      const decodedId = decodeURIComponent(interfaceIdParam);
      // Only auto-load if we haven't already loaded this interface
      if (!interfaceData || interfaceData.interface.uniqueId !== decodedId) {
        const interfaceToLoad = interfaces.find((i) => i.uniqueId === decodedId) || { uniqueId: decodedId, name: decodedId, switchHostname: '' };
        // Simulate autocomplete selection by calling handleInterfaceChange
        handleInterfaceChange({} as React.SyntheticEvent, interfaceToLoad);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, interfaces, interfaceData, loading]);

  /**
   * Transform InterfaceQueryResponse to GraphData format
   */
  const transformToGraphData = useCallback(
    (data: InterfaceQueryResponse): GraphData => {
      const nodes: GraphNode[] = [];
      const edges: GraphEdge[] = [];

      // Add Switch node
      nodes.push({
        id: data.switch.hostname,
        label: data.switch.hostname,
        type: 'switch',
        data: data.switch,
      });

      // Add Interface node (center)
      nodes.push({
        id: data.interface.uniqueId,
        label: data.interface.name,
        type: 'interface',
        data: data.interface,
      });

      // Add VLAN nodes
      data.vlans.forEach((vlan) => {
        nodes.push({
          id: `vlan-${vlan.vlanID}`,
          label: `VLAN ${vlan.vlanID}`,
          type: 'vlan',
          data: vlan,
        });
      });

      // Add Switch -> Interface edge (HAS_INTERFACE)
      edges.push({
        id: `${data.switch.hostname}-${data.interface.uniqueId}`,
        source: data.switch.hostname,
        target: data.interface.uniqueId,
        type: 'HAS_INTERFACE',
        label: '',
      });

      // Add Interface -> VLAN edges (using relationships)
      data.relationships.forEach((rel) => {
        edges.push({
          id: `${data.interface.uniqueId}-vlan-${rel.vlanId}`,
          source: data.interface.uniqueId,
          target: `vlan-${rel.vlanId}`,
          type: rel.type,
          label:
            rel.type === 'TRUNKS_VLAN' && rel.properties.native
              ? 'native'
              : rel.type === 'TRUNKS_VLAN'
              ? 'trunk'
              : 'member',
          data: {
            native: rel.properties.native,
          },
        });
      });

      return { nodes, edges };
    },
    []
  );

  /**
   * Handle interface selection from autocomplete
   * Implements hybrid approach: updates URL and fetches data immediately
   */
  const handleInterfaceChange = useCallback(
    async (_event: React.SyntheticEvent, value: string | InterfaceSummary | null) => {
      // If value is a string (typed but not selected), find matching interface
      let interfaceToFetch: InterfaceSummary | null = null;

      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) {
          // Find interface by uniqueId if user typed it
          interfaceToFetch = interfaces.find((i) => i.uniqueId === trimmed) || { uniqueId: trimmed, name: trimmed, switchHostname: '' };
        }
      } else {
        interfaceToFetch = value;
      }

      setSelectedInterface(interfaceToFetch);

      if (!interfaceToFetch) {
        setInterfaceData(null);
        setError(null);
        // Clear URL param
        setSearchParams({}, { replace: true });
        return;
      }

      // Update URL immediately (replace history to avoid back button clutter)
      setSearchParams({ id: encodeURIComponent(interfaceToFetch.uniqueId) }, { replace: true });

      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Fetch interface data
      setLoading(true);
      setError(null);
      setInterfaceData(null);

      try {
        const data = await api.getInterface(interfaceToFetch.uniqueId);

        // Only update state if this request wasn't aborted
        if (!controller.signal.aborted) {
          setInterfaceData(data);
          setError(null);
          // Scroll to top for better UX when switching interfaces
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (err) {
        // Don't show error if request was aborted (user changed selection)
        if (!controller.signal.aborted) {
          if (err instanceof Error) {
            setError(`Failed to fetch interface ${interfaceToFetch.uniqueId}: ${err.message}`);
          } else {
            setError(
              `Failed to fetch interface ${interfaceToFetch.uniqueId}. Please check if the backend is running.`
            );
          }
          setInterfaceData(null);
        }
      } finally {
        // Only clear loading if this request wasn't aborted
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [interfaces, setSearchParams]
  );

  /**
   * Export data as CSV
   */
  const handleExportCSV = useCallback(() => {
    if (!interfaceData) return;

    // Build CSV content with interface info header
    const interfaceInfo = [
      `Interface: ${interfaceData.interface.name}`,
      `Unique ID: ${interfaceData.interface.uniqueId}`,
      `Mode: ${interfaceData.interface.mode || 'unknown'}`,
      `Status: ${interfaceData.interface.status || 'unknown'}`,
      `Parent Switch: ${interfaceData.switch.hostname}`,
      '',
    ];

    const headers = ['VLAN ID', 'VLAN Name', 'Status', 'Relationship Type', 'Native VLAN'];
    const rows = interfaceData.vlans.map((vlan) => {
      const rel = interfaceData.relationships.find((r) => r.vlanId === vlan.vlanID);
      return [
        vlan.vlanID.toString(),
        vlan.name || 'N/A',
        vlan.status || 'unknown',
        rel?.type || 'N/A',
        rel?.properties.native ? 'Yes' : 'No',
      ];
    });

    const csvContent = [
      ...interfaceInfo,
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `interface-${interfaceData.interface.uniqueId}-vlans.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [interfaceData]);

  /**
   * Export data as JSON
   */
  const handleExportJSON = useCallback(() => {
    if (!interfaceData) return;

    const jsonContent = JSON.stringify(interfaceData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `interface-${interfaceData.interface.uniqueId}-data.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [interfaceData]);

  /**
   * Get relationship info for a VLAN
   */
  const getRelationshipInfo = useCallback(
    (vlanId: number): InterfaceRelationshipInfo | undefined => {
      return interfaceData?.relationships.find((r) => r.vlanId === vlanId);
    },
    [interfaceData]
  );

  /**
   * Memoized graph data to prevent unnecessary re-renders
   */
  const graphData = useMemo(() => {
    if (!interfaceData) return { nodes: [], edges: [] };
    return transformToGraphData(interfaceData);
  }, [interfaceData, transformToGraphData]);

  /**
   * Handle table sort
   */
  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortOrder('asc');
      }
    },
    [sortField, sortOrder]
  );

  /**
   * Sort VLANs
   */
  const sortedVlans = useCallback(
    (vlans: VLAN[]): VLAN[] => {
      return [...vlans].sort((a, b) => {
        let aValue: string | number = '';
        let bValue: string | number = '';

        switch (sortField) {
          case 'vlanId':
            aValue = a.vlanID;
            bValue = b.vlanID;
            break;
          case 'name':
            aValue = a.name || '';
            bValue = b.name || '';
            break;
          case 'status':
            aValue = a.status || '';
            bValue = b.status || '';
            break;
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    },
    [sortField, sortOrder]
  );

  /**
   * Cleanup: abort any in-flight requests on unmount
   */
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Interface Detail View
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Search for an interface to view its properties, parent switch, and VLAN memberships.
      </Typography>

      {/* Search Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Autocomplete
            options={interfaces}
            value={selectedInterface}
            onChange={handleInterfaceChange}
            open={autocompleteOpen}
            onOpen={() => setAutocompleteOpen(true)}
            onClose={() => setAutocompleteOpen(false)}
            groupBy={(option) => option.switchHostname}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              const mode = option.mode ? ` (${option.mode})` : '';
              const status = option.status ? ` - ${option.status}` : '';
              return `${option.name}${mode}${status}`;
            }}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box component="li" key={key} {...otherProps}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.uniqueId}
                      {option.mode && ` • ${option.mode}`}
                      {option.status && ` • ${option.status}`}
                      {option.description && ` • ${option.description}`}
                    </Typography>
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={autocompleteInputRef}
                label="Select Interface"
                placeholder="Type interface name or ID to search..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {interfacesLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            loading={interfacesLoading}
            disabled={loading}
            freeSolo
            sx={{ flexGrow: 1 }}
          />
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Interface Data */}
      {interfaceData && (
        <>
          {/* Interface Properties */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Interface Properties</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Export as CSV">
                    <IconButton size="small" onClick={handleExportCSV}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export as JSON">
                    <IconButton size="small" onClick={handleExportJSON}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Interface Name
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {interfaceData.interface.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Unique ID
                  </Typography>
                  <Typography variant="body1">{interfaceData.interface.uniqueId}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Mode
                  </Typography>
                  <Chip
                    label={interfaceData.interface.mode || 'unknown'}
                    size="small"
                    color={
                      interfaceData.interface.mode === 'access'
                        ? 'success'
                        : interfaceData.interface.mode === 'trunk'
                        ? 'info'
                        : 'default'
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {interfaceData.interface.status === 'up' ? (
                      <CheckCircleIcon fontSize="small" color="success" />
                    ) : (
                      <CancelIcon fontSize="small" color="error" />
                    )}
                    <Chip
                      label={interfaceData.interface.status || 'unknown'}
                      size="small"
                      color={interfaceData.interface.status === 'up' ? 'success' : 'error'}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">{interfaceData.interface.description || 'N/A'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Parent Switch */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <RouterIcon color="primary" />
                <Typography variant="h6">Parent Switch</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Hostname
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {interfaceData.switch.hostname}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Management IP
                  </Typography>
                  <Typography variant="body1">{interfaceData.switch.managementIp || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Vendor
                  </Typography>
                  <Typography variant="body1">{interfaceData.switch.vendor || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Model
                  </Typography>
                  <Typography variant="body1">{interfaceData.switch.model || 'N/A'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* LAG Membership (Phase 2 - placeholder) */}
          {interfaceData.lag && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  LAG Membership
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      LAG Unique ID
                    </Typography>
                    <Typography variant="body1">{interfaceData.lag.uniqueId}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      LAG Name
                    </Typography>
                    <Typography variant="body1">{interfaceData.lag.name || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Graph Visualization */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Graph Visualization
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Interactive graph showing interface with parent switch and connected VLANs. Green interface =
                access, Blue interface = trunk.
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <GraphVisualization
                  ref={graphContainerRef}
                  data={graphData}
                  height="500px"
                  layout="breadthfirst"
                  centerNodeId={interfaceData.interface.uniqueId}
                />
                <GraphControls containerRef={graphContainerRef} />
              </Box>
            </CardContent>
          </Card>

          {/* VLAN Membership Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                VLAN Membership ({interfaceData.vlans.length}{' '}
                {interfaceData.vlans.length === 1 ? 'VLAN' : 'VLANs'})
              </Typography>
              {interfaceData.vlans.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No VLANs associated with this interface.
                </Typography>
              ) : (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={sortField === 'vlanId'}
                            direction={sortField === 'vlanId' ? sortOrder : 'asc'}
                            onClick={() => handleSort('vlanId')}
                          >
                            VLAN ID
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortField === 'name'}
                            direction={sortField === 'name' ? sortOrder : 'asc'}
                            onClick={() => handleSort('name')}
                          >
                            VLAN Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortField === 'status'}
                            direction={sortField === 'status' ? sortOrder : 'asc'}
                            onClick={() => handleSort('status')}
                          >
                            Status
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Relationship</TableCell>
                        <TableCell align="center">Native VLAN</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedVlans(interfaceData.vlans).map((vlan) => {
                        const rel = getRelationshipInfo(vlan.vlanID);
                        const isNative = rel?.properties.native === true;
                        const isTrunk = rel?.type === 'TRUNKS_VLAN';

                        return (
                          <TableRow
                            key={vlan.vlanID}
                            sx={{
                              backgroundColor: isNative ? 'rgba(237, 108, 2, 0.08)' : 'inherit',
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {vlan.vlanID}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{vlan.name || 'N/A'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={vlan.status || 'unknown'}
                                size="small"
                                color={vlan.status === 'active' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={rel?.type || 'N/A'}
                                size="small"
                                variant="outlined"
                                color={rel?.type === 'MEMBER_OF' ? 'success' : 'info'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {isTrunk && isNative ? (
                                <Chip label="Native" size="small" color="warning" />
                              ) : isTrunk ? (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  N/A
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};
